'use client'

import Link from 'next/link'
import { Badge } from 'flowbite-react'
import { formatDate } from '@/utils/formatDate'
import { numberFormat } from '@/utils/formatNumber'
import { BountyListItem } from '../actions'

/** Generate a deterministic color from host name for the banner. */
const BANNER_COLORS = [
  'from-[#1a2332] to-[#2c3e50]',
  'from-[#1a3a2e] to-[#2d6a4f]',
  'from-[#3b1a45] to-[#5c2d82]',
  'from-[#2c1810] to-[#5d3a1a]',
  'from-[#0c2340] to-[#1a4a7a]',
]

function getBannerColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return BANNER_COLORS[Math.abs(hash) % BANNER_COLORS.length]
}

interface BountyCardProps {
  bounty: BountyListItem
}

export default function BountyCard({ bounty }: BountyCardProps) {
  const isOpen = new Date(bounty.endDate) > new Date()
  const bannerColor = getBannerColor(bounty.hostName || bounty.title)

  // Extract tags from scope (first few bullet points)
  const scopeTags =
    bounty.scope?.inScope
      ?.split('\n')
      .slice(0, 3)
      .map((line) => line.replace(/^-\s*/, '').split('(')[0].trim())
      .filter((t) => t.length > 0 && t.length < 30) ?? []

  return (
    <Link href={`/web/bug-bounty/${bounty.slug}`} className="relative">
      <div className="hover:cursor-pointer relative rounded-3xl overflow-hidden border h-full">
        {/* Banner */}
        <div
          className={`relative flex items-center justify-center w-full h-40 bg-gradient-to-br ${bannerColor}`}
        >
          {/* Status badge */}
          <div className="absolute top-3 right-3">
            <span
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                bounty.visibility === 'invite_only'
                  ? 'bg-purple-100 text-purple-800'
                  : isOpen
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-200 text-gray-600'
              }`}
            >
              {bounty.visibility === 'invite_only'
                ? 'Invite only'
                : isOpen
                  ? 'Open'
                  : 'Closed'}
            </span>
          </div>

          {/* Host name */}
          {bounty.hostLogo ? (
            <img
              src={bounty.hostLogo}
              alt={bounty.hostName || ''}
              className="h-12 w-12 rounded-full"
            />
          ) : (
            <span className="text-xl font-bold text-white tracking-wider uppercase">
              {bounty.hostName || 'Bug Bounty'}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          <h5 className="text-xl font-semibold tracking-tight text-gray-900 line-clamp-2">
            {bounty.title}
          </h5>

          {bounty.description && (
            <p className="h-11 mt-2.5 line-clamp-2 text-gray-500">
              {bounty.description}
            </p>
          )}

          {/* Tags */}
          {scopeTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 line-clamp-1">
              {scopeTags.map((tag, index) => (
                <Badge
                  key={index}
                  className="px py-0.5 bg-gray-100 font-medium text-xs text-gray-900 rounded-md"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Rewards & close date */}
          <div className="flex justify-between items-end mt-4 mb-4">
            <div>
              <span className="text-xs text-gray-500">Rewards up to</span>
              <p className="text-lg font-bold text-green-700">
                {bounty.rewardMax != null
                  ? `$${numberFormat(bounty.rewardMax)}`
                  : 'TBD'}
              </p>
            </div>
            <div className="text-right">
              <span className="text-xs text-gray-500">Submissions close</span>
              <p className="text-sm font-semibold text-gray-900">
                {formatDate(bounty.endDate, 'd MMM yyyy')}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center pt-3 border-t border-gray-100">
            <span className="text-sm text-gray-500">
              <strong>{numberFormat(bounty.participantCount)}</strong>{' '}
              participating
            </span>
            <span className="text-sm text-gray-500">
              {bounty.visibility === 'invite_only' ? 'Invite only' : 'Public'}
            </span>
          </div>
        </div>
      </div>
    </Link>
  )
}
